import globals from "@lvt/globals";
import { unwrap } from "@lvt/web-data";
import {
    Card,
    CardHeader,
    Divider,
    Link,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Fab,
    Popper,
    Fade,
    makeStyles,
    IconButton,
    Grid,
    Tooltip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import LaunchIcon from "@material-ui/icons/Launch";
import SecurityIcon from "@material-ui/icons/Security";
import clsx from "clsx";
import { useState } from "react";

import { useSelector } from "@/store";
import * as FullStory from "@/utilities/hooks/fullstory";
import { OpenButton } from "@/utilities/types/streaming";

import useActionButtonStyles from "../ActionButton.styles";
import AddPreset from "./AddPreset";
import PresetsMenuItem from "./PresetsMenuItem";


const useStyles = makeStyles((theme) => {
    return {
        close: {
            position: "absolute",
            top: 8,
            right: 8,
            color: theme.palette.primary.contrastText,
        },
        divider: {
            backgroundColor: theme.palette.primary.light,
        },
        menu: {
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.primary.contrastText,
            borderRadius: 8,
        },
        popper: {
            zIndex: theme.zIndex.modal,
            marginBottom: 16,
            width: 300,
            marginRight: "0.625rem",
        },
        presetList: {
            maxHeight: 400,
            overflowX: "auto",
        },
        presetListMosaic: {
            maxHeight: 250,
            overflowX: "auto",
        },
        settingsLink: {
            "&:hover": {
                backgroundColor: theme.palette.primary.light,
            },
        },
    };
});

export interface Preset {
    id: number;
    name: string;
}

interface PresetsButtonProps {
    allowSelfService?: boolean,
    menuAnchor: HTMLElement | null;
    handleMenu: (button: OpenButton) => void;
    liveUnitId: number;
    open: boolean;
    presets?: Preset[];
    variant?: "mosaic" | "default";
    maxMenuHeight?: number
    passedCameraId?: number
}

const PresetsButton: React.FC<PresetsButtonProps> = ({ allowSelfService, menuAnchor, handleMenu, liveUnitId, open, presets, variant = "default", maxMenuHeight, passedCameraId }) => {
    const actionButtonClasses = useActionButtonStyles();
    const styles = useStyles();
    const [showTooltip, setShowTooltip] = useState(false);
    const meta = useSelector((state) => unwrap(state.stream.streamMeta));
    const cameraId = passedCameraId ? passedCameraId : useSelector((state) => state.cameras.streamingId);

    if (!presets) presets = meta?.presets;

    const id = open ? "popper" : undefined;

    const handleClick = () => {
        setShowTooltip(false);
        handleMenu(OpenButton.Presets);
    };

    return (
        <>
            <Popper
                id={id}
                open={open}
                disablePortal
                anchorEl={menuAnchor}
                transition
                placement="top-end"
                className={styles.popper}
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={350}>
                        <Card raised classes={{ root: styles.menu }}>
                            <IconButton aria-label="close" onClick={() => handleMenu(OpenButton.None)} classes={{ root: styles.close }}>
                                <CloseIcon />
                            </IconButton>
                            <CardHeader
                                title="Presets"
                                data-testid="presets-header"
                                classes={{ title: variant === "mosaic" ? actionButtonClasses.mosaicHeader : undefined }}
                            />
                            <Divider className={styles.divider} />
                            <List aria-label="preset-list" classes={{ root: variant === "mosaic" ? styles.presetListMosaic : styles.presetList }} style={{ maxHeight: maxMenuHeight }}>
                                {presets?.map((preset) => (
                                    <PresetsMenuItem
                                        allowSelfService={allowSelfService}
                                        key={preset.id}
                                        handleMenu={handleMenu}
                                        liveUnitId={liveUnitId}
                                        preset={preset}
                                        showEdit={variant !== "mosaic"}
                                        variant={variant}
                                        cameraId={cameraId}
                                    />
                                ))}
                                {allowSelfService && (<>
                                    <AddPreset liveUnitId={liveUnitId} />
                                    <Link href={`${globals.servicePortal.url}/camera/${cameraId}`} underline="none" color="inherit" target="_blank" rel="noopener noreferrer">
                                        <ListItem className={styles.settingsLink}>
                                            <ListItemText primary="Advanced Settings" />
                                            <ListItemSecondaryAction>
                                                <LaunchIcon />
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    </Link>
                                </>)}
                            </List>
                        </Card>
                    </Fade>
                )}
            </Popper>
            <Grid item className={actionButtonClasses.fabItem}>
                <Tooltip
                    PopperProps={{ disablePortal: true }}
                    open={showTooltip}
                    disableHoverListener
                    onMouseEnter={() => !open && setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    title="Presets"
                    placement="top"
                >
                    <Fab
                        className={clsx(
                            actionButtonClasses.fab,
                            open && actionButtonClasses.active,
                            open && FullStory.names.classes.streaming.presetMenu,
                        )}
                        aria-label="presets"
                        onClick={handleClick}
                    >
                        <SecurityIcon />
                    </Fab>
                </Tooltip>
            </Grid>
        </>
    );
};
export default PresetsButton;
